/**
 * When the valley pushes back (M5-02, and the seam M5-04 reuses).
 *
 * Everything in the game so far happens because a player did something. This
 * module is the other direction: a small scheduler that lets the *world* start
 * things, on conditions read out of world state, at a rate somebody can reason
 * about a week in advance.
 *
 * It is deliberately about forty lines of actual rule, and the restraint is the
 * design. What a `PressureRule` may do is:
 *
 * - say whether its conditions hold right now (`when`),
 * - say how likely it is *given* that they hold (`chance`),
 * - and do something once, when the dice say so (`fire`).
 *
 * What it may **not** do is keep state of its own. The only thing remembered
 * between firings is when each rule last fired and how often it has fired
 * lately (`pressure.ts`), because those are the two facts a governor needs and
 * neither belongs to any one rule. A rule that wanted more memory than that
 * would be a system, and should be written as one.
 *
 * ## Why it is in the sim rather than in the server
 *
 * Because it rolls dice, and everything that rolls dice has to be in the
 * replay. A scheduler the room kept to itself would mean a recorded session
 * replays into a valley where the wolves never came.
 */
import { pressureFault, pressureFor } from './pressure.js';
/**
 * The system that runs a set of rules, on the hour.
 *
 * On the hour because that is the cadence the probabilities are quoted at, and
 * because a scheduler asking forty questions of world state ten times a second
 * would be forty questions a second spent on an answer that changes at dusk.
 *
 * Rules are run in the order given, each on its own RNG substream keyed by its
 * id — so adding a second rule cannot shift the dice the first one draws, which
 * is the property that lets M5-04's boar arrive without moving M5-02's wolves.
 */
export function pressureSystem(rules) {
    return {
        name: 'pressure',
        phase: 'hour',
        run(ctx) {
            const world = ctx.world;
            world.pressure ??= {};
            for (const rule of rules) {
                const state = pressureFor(world.pressure, rule.id, ctx.tick);
                if (pressureFault(state, rule, ctx.tick) !== null)
                    continue;
                if (!rule.when(ctx))
                    continue;
                const chance = rule.chance(ctx);
                if (chance <= 0)
                    continue;
                const rng = ctx.rng(`pressure:${rule.id}`);
                if (!rng.chance(Math.min(1, chance)))
                    continue;
                state.lastAt = ctx.tick;
                state.count += 1;
                rule.fire(ctx, rng);
            }
        },
    };
}
//# sourceMappingURL=scheduler.js.map